import { IRewardFlow } from '../interfaces/IRewardFlow';
import { PurchaseTypes } from '../store/PurchaseTypes';

export const PlayerCountUpsellKeys = {
    FlowAlias: 'PlayerCountUpsell',
    MinimumTicketCount: 'MinimumTicketCount',
    WasOfferShown: 'WasOfferShown',
    WasOfferAccepted: 'WasOfferAccepted',
} as const;

export interface IPlayerCountUpsellConfig {
    isActive: boolean;
    minimumTicketCount: number;
}

export class PlayerCountUpsellFlow {
    private config: IPlayerCountUpsellConfig;

    constructor(flows: IRewardFlow[]) {
        const flow = flows.find((f) => f.alias === PlayerCountUpsellKeys.FlowAlias);
        if (!flow) {
            this.config = { isActive: false, minimumTicketCount: 0};
            return;
        }
        const minSetting = flow.settings.find((s) => s.settingKey === PlayerCountUpsellKeys.MinimumTicketCount);
        const minimumTicketCount = minSetting ? parseInt(minSetting.settingValue, 10) || 0 : 0;
        this.config = {
            isActive: true,
            minimumTicketCount
        };
    }

    get isActive(): boolean {
        return this.config.isActive;
    }

    get minimumTicketCount(): number {
        return this.config.minimumTicketCount;
    }


    /**
     * Should the upsell widget be shown?
     *
     * Checks: flow active, standard booking (not private), ZL Arena attraction,
     * player count below threshold, and enough session capacity to reach threshold.
     *
     * Server-side also checks: authenticated player, master booking -- those are
     * validated when the contribution is recorded after reserve.
     */
    isEligible(
        featureFlagEnabled: boolean,
        selectedPlayers: number | undefined,
        purchaseType: PurchaseTypes,
        attractionTypeId: number | undefined,
        isPrivateUpgrade: boolean,
        sessionRemainingSlots: number | undefined,
        maximumPlayers: number
    ): boolean {
        if (
            !featureFlagEnabled ||
            !this.isActive ||
            this.minimumTicketCount <= 0 ||
            purchaseType !== PurchaseTypes.Booking ||
            isPrivateUpgrade ||
            !this.isPackageEligible(attractionTypeId) ||
            selectedPlayers === undefined ||
            selectedPlayers <= 0 ||
            selectedPlayers >= this.minimumTicketCount
        ) {
            return false;
        }

        const effectiveMax = Math.min(maximumPlayers, sessionRemainingSlots ?? 0);
        return effectiveMax >= this.minimumTicketCount;
    }

    isPackageEligible(attractionTypeId: number | undefined): boolean {
        return attractionTypeId === 1;
    }

    isOfferMet(playerCount: number): boolean {
        return this.isActive && playerCount >= this.minimumTicketCount;
    }
}
